/**
 * Component will display the list of all brands
 *
 * @module views/components/BrandListSection
 * @memberof -Common
 */
import './BrandListSection.scss';

import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';

import Icon from '@ulta/core/components/Icon/Icon';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';

import * as utils from '@ulta/hooks/useAnchorList/useAnchorList';

/**
  * Represents a BrandListSection component
  *
  * @method
  * @param {BrandListSectionProps} props - React properties passed from composition
  * @returns BrandListSection
  */

export const BrandListSection = React.forwardRef( function( props, ref ){
  const sectionEle = useRef();
  const navEle = useRef();
  const id = `${props.brand?.title}` === '#' ? `Hashsection` : `${props.brand?.title}section`;
  const { breakpoint } = useDeviceInflection();
  const itemCountPerColumn = useRef( 1 );


  const getNoOfColumns = ( breakpoint ) => {
    switch ( breakpoint ){
      case 'XL':
        return 4;
      case 'LG':
      case 'MD':
        return 3;
      default:
        return 1;
    }
  };
  const noOfColumns = getNoOfColumns( breakpoint.CURRENT_BREAKPOINT );

  useEffect( () => {
    const itemLength = Number( props.brand?.items?.length ) > 0 ? props.brand?.items?.length : 0;
    itemCountPerColumn.current = itemLength > noOfColumns ? ~~( itemLength / noOfColumns ) + itemLength % noOfColumns : 1;
  }, [noOfColumns] );

  useEffect( () => {
    document.addEventListener( 'scroll', highlightOnScroll );
    sectionEle.current = document.getElementById( id );
    navEle.current = document.getElementsByClassName( 'AnchorList' )[0];
    return () => {
      document.removeEventListener( 'scroll', highlightOnScroll );
    };
  }, [] );

  const highlightOnScroll = () => {
    if( !props.reportActive || !navEle.current || !sectionEle.current ){
      return;
    }

    const brandSection = sectionEle.current?.getBoundingClientRect()?.top;
    const nav = navEle.current?.getBoundingClientRect()?.top;
    if( brandSection <= nav + ANCHOR_LIST_HEIGHT && brandSection >= nav ){
      props.onScrolled && props.onScrolled( props.brand.title );
    }
  };

  const colRenderer = ( items )=>{
    return items?.map( ( item, i ) => (
      <li key={ i }>
        <Link_Huge
          url={ item.action?.url }
          secondary
          compact
        >
          { item.action?.label }
        </Link_Huge>
        <span className='BrandListSection__listItemIcon'>
          { item?.icons?.length && item?.icons.map( ( icon, i )=>(
            <Icon
              key={ i }
              size={ 'lg' }
              name={ icon }
              aria-hidden={ true }
            />
          ) ) }
        </span>
      </li>
    ) );
  };

  return (
    props.brand?.items?.length > 0 && (
      <div className='BrandListSection'
        id={ id }
      >
        <div className='BrandListSection__BrandName'>
          <Text textStyle='title-6'
            textAlign='center'
          >
            { props.brand?.title }
          </Text>
        </div>
        <div className='BrandListSection__Listing'>
          { utils.ColumnSort( { items:props.brand, breakpoint }, { colRenderer, getNoOfColumns } ) }
        </div>
      </div>
    )
  );
} );


/**
  * Property type definitions
  * @typedef BrandListSectionProps
  * @type {object}
  * @property {object} brand - object contains brands title and action
  */
export const propTypes = {
  brand: PropTypes.object
};

BrandListSection.propTypes = propTypes;
BrandListSection.displayName = 'BrandListSection';

export default BrandListSection;

/**
  * Height of the brand index a-z navigation element
  * @constant {number}
  * @default
  */
export const ANCHOR_LIST_HEIGHT = 165;
